import { toPascalCase } from '@sg-core/resources/assets/js/utils';

interface ComponentModule {
  default?: unknown;
  [key: string]: unknown;
}

export abstract class ComponentRegistry {
  private modules: Record<string, () => Promise<unknown>>;
  private readonly nameToPath = new Map<string, string>();
  private readonly cache = new Map<string, unknown>();

  constructor() {
    this.modules = this.getModules();
    this.init();
  }

  protected abstract getModules(): Record<string, () => Promise<unknown>>;

  protected init() {
    for (const fullPath of Object.keys(this.modules)) {
      const name = this.deriveName(fullPath);

      if (name) {
        this.nameToPath.set(name, fullPath);
      }
    }
  }

  /**
   * Automatically derive a component name from the file path.
   */
  private deriveName(path: string): string | null {
    const parts = path.split('/');
    const file = parts.pop();

    if (!file) {
      throw new Error(`Invalid component path: ${path}`);
    }

    const [base] = file.split('.');

    if (base === 'index') {
      const parentFolder = parts.at(-1);
      return parentFolder ? toPascalCase(parentFolder) : null;
    }

    return toPascalCase(base);
  }

  /**
   * Load a component by name, optionally specifying a named export.
   */
  async get<T = unknown>(name: string, exportName?: string): Promise<T> {
    const cacheKey = exportName ? `${name}.${exportName}` : name;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as T;
    }

    const path = this.nameToPath.get(name);

    if (!path) {
      throw new Error(`Component "${name}" is not registered.`);
    }

    const importer = this.modules[path];

    if (!importer) {
      throw new Error(`No module found at "${path}".`);
    }

    const module = (await importer()) as ComponentModule;
    const exported = exportName ? module[exportName] : (module[name] ?? module.default ?? module);

    if (!exported) {
      throw new Error(`Export "${exportName ?? 'default'}" not found in module "${name}" (${path})`);
    }

    this.cache.set(cacheKey, exported);

    return exported as T;
  }
}
